// Les outils de modération : deux prédicats et la fenêtre de suspension.
//
// Sortis de `views/moderation.js`, dont l'écran est passé à React. Ils sont
// appelés d'un peu partout — la page profil, le menu, l'écran de modération —
// et les laisser dans le fichier de l'écran obligeait le composant à importer
// celui qui l'importe.
//
// `estModerateur` et `estSuspendu` sont les deux questions que l'application se
// pose le plus souvent. Elles sont écrites ici une fois, et nulle part ailleurs.

import Store from "../store.js";
import { esc, toast, modale, fermerModale } from "../ui.js";

export const estModerateur = (u) => ["moderateur", "admin"].includes(u?.role);
export const estSuspendu = (u) => Boolean(u?.suspendu && u.suspendu > Date.now());

const DUREES = [
  { jours: 1, label: "1 jour" },
  { jours: 7, label: "7 jours" },
  { jours: 30, label: "30 jours" },
  { jours: 0, label: "Définitive" },
];

/** Boîte de suspension, appelée depuis la modération comme depuis un profil. */
export function suspendreMembre(membre, apres) {
  const { el } = modale({
    titre: `Suspendre ${membre.nom}`,
    corps: `
      <p class="texte-doux petit">Le membre pourra encore lire la communauté, mais ne pourra plus publier,
      commenter, envoyer de message ni demander d'amitié.</p>
      <label class="label">Durée
        <div class="filtres" id="durees">
          ${DUREES.map((d, i) => `<button class="chip${i === 1 ? " est-actif" : ""}" data-jours="${d.jours}">${esc(d.label)}</button>`).join("")}
        </div>
      </label>
      <label class="label">Motif (transmis au membre)
        <textarea class="champ" id="motif-suspension" rows="2" maxlength="200" placeholder="Ex : propos contraires à la charte"></textarea>
      </label>`,
    actions: [
      { label: "Annuler" },
      {
        label: "Suspendre",
        classe: "btn--danger",
        onClick: async (fond, fermer) => {
          const jours = Number(fond.querySelector("[data-jours].est-actif")?.dataset.jours ?? 7);
          const motif = fond.querySelector("#motif-suspension").value.trim();
          fermer();
          try {
            await Store.moderation.suspendre(membre.uid, { jours, motif });
            toast(`${membre.nom} est suspendu${jours ? ` ${jours} jour${jours > 1 ? "s" : ""}` : " définitivement"}`);
            apres?.();
          } catch (e) {
            toast(e.message || "Suspension impossible", "erreur");
          }
        },
      },
    ],
  });
  el.querySelector("#durees").addEventListener("click", (e) => {
    const b = e.target.closest("[data-jours]");
    if (!b) return;
    el.querySelectorAll("[data-jours]").forEach((x) => x.classList.toggle("est-actif", x === b));
  });
}

/* ═══════════════════════════════════ SUPPRIMER DÉFINITIVEMENT UN COMPTE ══ */

/**
 * La suppression définitive — celle qui n'est pas une restriction.
 *
 * ⚠️ ELLE DEMANDE D'ÉCRIRE LE NOM DU MEMBRE, et pas seulement de confirmer.
 * Un bouton « Êtes-vous sûr ? » se clique sans lire ; recopier un nom oblige à
 * regarder DE QUI l'on parle. C'est la même précaution que « Supprimer mon
 * compte » demande déjà à chacun pour lui-même — il n'y a pas de raison d'être
 * moins prudent quand on décide pour autrui.
 *
 * On dit aussi, en toutes lettres, ce qui NE sera pas effacé : promettre plus
 * que ce qu'on fait serait la pire façon de rendre ce bouton.
 */
export function supprimerCompteMembre(membre, apres) {
  const attendu = (membre.nom || "").trim();
  const { el } = modale({
    titre: `Supprimer définitivement ${esc(membre.nom)}`,
    corps: `
      <p>Cette action est <strong>définitive</strong>. Seront effacés :</p>
      <ul class="details" style="margin:12px 0">
        <li>son profil, sa photo et sa couverture</li>
        <li>ses publications et ses statuts</li>
        <li>son attestation de baptême et ses coordonnées</li>
      </ul>
      <p class="texte-doux petit"><strong>Ce qui n'est pas effacé :</strong> les messages privés
      qu'il a envoyés — ils appartiennent aussi à ceux qui les ont reçus — et son adresse de
      connexion, que seule la console Firebase peut retirer. Il ne pourra plus entrer : Koinonia
      lui répondra que son compte a été supprimé.</p>
      <label class="label">Pour confirmer, écrivez <strong>${esc(attendu)}</strong>
        <input class="champ" id="nom-confirmation" autocomplete="off" placeholder="${esc(attendu)}">
      </label>`,
    actions: [
      { label: "Annuler" },
      {
        label: "Supprimer définitivement",
        classe: "btn--danger",
        onClick: async (fond) => {
          const saisi = fond.querySelector("#nom-confirmation").value.trim();
          if (saisi.toLowerCase() !== attendu.toLowerCase())
            return toast(`Écrivez « ${attendu} » pour confirmer`, "erreur");
          const bouton = fond.querySelector('[data-action="1"]');
          bouton.disabled = true;
          bouton.textContent = "Suppression…";
          try {
            await Store.moderation.supprimerCompte(membre.uid);
            fermerModale();
            toast("Compte supprimé définitivement");
            apres?.();
          } catch (e) {
            bouton.disabled = false;
            bouton.textContent = "Supprimer définitivement";
            toast(e.message || "Suppression impossible", "erreur");
          }
        },
      },
    ],
  });
  return el;
}

/* ⚠️ RENOMMER UN MEMBRE — 24 octobre 2026, la régularisation des noms
   (demandée par Josaphat). La modération renomme sans attendre le semestre ;
   le serveur n'accepte d'elle qu'un nom au format réel, et l'horloge du
   membre ne bouge pas. En cas de refus, la fenêtre RESTE ouverte : on
   corrige sa saisie au lieu de tout retaper. */
export function renommerMembre(membre, apres) {
  modale({
    titre: `Renommer ${membre.nom}`,
    corps: `
      <p class="texte-doux petit">Le nouveau nom doit être un vrai nom :
      prénom et nom, en lettres seulement — sans chiffres, symboles ni
      titres. Le membre garde son droit de changement semestriel.</p>
      <label class="label">Nouveau nom
        <input class="champ" id="nouveau-nom" maxlength="60" value="${esc(membre.nom || "")}">
      </label>`,
    actions: [
      { label: "Annuler" },
      {
        label: "Renommer",
        onClick: async (fond, fermer) => {
          const nom = fond.querySelector("#nouveau-nom").value;
          try {
            await Store.moderation.renommer(membre.uid, nom);
            fermer();
            toast("Nom régularisé");
            apres?.();
          } catch (e) {
            toast(e.message || "Renommage impossible", "erreur");
          }
        },
      },
    ],
  });
}
